import { useMemo } from 'react';
import { BREAKPOINTS } from '../constants/breakpoints';
import { useMediaQuery } from './useMediaQuery';

/**
 * Tailwind CSS breakpoints (see constants/breakpoints.js).
 * Product tier `isMobile` is < 768px (md), not sm (640px).
 */

/**
 * Custom hook that provides current breakpoint and device type information
 * @returns {Object} Breakpoint state with breakpoint name and device flags
 */
export function useBreakpoint() {
  const isSm = useMediaQuery(`(min-width: ${BREAKPOINTS.sm}px)`);
  const isMd = useMediaQuery(`(min-width: ${BREAKPOINTS.md}px)`);
  const isLg = useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px)`);
  const isXl = useMediaQuery(`(min-width: ${BREAKPOINTS.xl}px)`);

  const breakpointState = useMemo(() => {
    // Determine current breakpoint
    let breakpoint = 'xs';
    if (isXl) breakpoint = 'xl';
    else if (isLg) breakpoint = 'lg';
    else if (isMd) breakpoint = 'md';
    else if (isSm) breakpoint = 'sm';

    return {
      breakpoint,
      // Device type flags
      isMobile: !isMd,           // < 768px (aligned with sidebar, browser banner, ResponsiveTable)
      isTablet: isMd && !isLg,   // 768px - 1023px
      isDesktop: isLg,           // >= 1024px
      // Individual breakpoint flags
      isXs: !isSm,
      isSm: isSm && !isMd,
      isMd: isMd && !isLg,
      isLg: isLg && !isXl,
      isXl: isXl,
      // "At least" breakpoint flags (useful for responsive rendering)
      isSmUp: isSm,
      isMdUp: isMd,
      isLgUp: isLg,
      isXlUp: isXl,
    };
  }, [isSm, isMd, isLg, isXl]);

  return breakpointState;
}

export default useBreakpoint;
